/**
 * Maps route path prefixes to the permission required to access them.
 * Entries are matched by `startsWith` — e.g. "/products" covers "/products/:id" too.
 * Routes not listed here are accessible to all authenticated users (e.g. dashboard).
 *
 * Create routes inherit the parent's view permission by default; override with a
 * more specific prefix if a create-specific permission is needed.
 */
export const ROUTE_PERMISSIONS: [prefix: string, permission: string][] = [
  // Notifications
  ["/notifications", "view_notification"],

  // Users
  ["/users/invite", "create_user"],
  ["/users", "view_users"],

  // Roles
  ["/roles/create", "create_role"],
  ["/roles", "view_role"],

  // Customers
  ["/customers/create", "create_customer"],
  ["/customers", "view_customer"],

  // Orders
  ["/orders/create", "create_order"],
  ["/orders", "view_order"],

  // Scheduling
  ["/scheduling/create", "create_order"],
  ["/scheduling", "view_scheduling"],

  // Jobs & Tasks
  ["/jobs/recipes/new", "create_job"],
  ["/jobs/recipes", "view_job"],
  ["/jobs/new", "create_job"],
  ["/jobs/create", "create_job"],
  ["/jobs", "view_job"],
  ["/tasks", "view_job"],

  // Inventories
  ["/inventories", "view_inventory"],

  // Product Mixes
  ["/product-mixes", "manage_operation_product_mix"],

  // Log Addresses
  ["/log-addresses", "view_log_addresses"],

  // Projected Stock
  ["/projected-stock", "view_product"],

  // Catalog
  ["/taxonomies", "view_taxo"],
  ["/products", "view_product"],
  ["/skus", "view_sku"],
  ["/manufacturers", "view_manufacturer"],
  ["/vendors", "view_vendor"],
  ["/end-customers", "view_end_customer"],
  ["/vehicles", "view_vehicle"],
  ["/addresses", "view_address"],

  // Settings
  ["/operations/create", "create_operation"],
  ["/operations", "view_operation"],
  ["/operation-types", "view_operation_type"],
  ["/labels", "view_label"],

  // Admin
  ["/user-associations", "create_user_org_association"],
  ["/entities", "view_entity"],
  ["/orgs/create", "create_org"],
  ["/orgs", "view_org"],
  ["/address-types", "view_address_type"],
  ["/unit-groups", "view_unit_group"],
];

/**
 * Find the required permission for a given pathname.
 * Returns undefined if no permission is required (public authenticated route).
 * More specific prefixes must come first in the array (e.g. "/users/invite" before "/users").
 */
export function getRoutePermission(pathname: string): string | undefined {
  for (const [prefix, permission] of ROUTE_PERMISSIONS) {
    if (pathname === prefix || pathname.startsWith(prefix + "/")) {
      return permission;
    }
  }
  return undefined;
}
